import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Users,
  Link as LinkIcon,
  Unlink,
  Settings,
  Activity,
  Package,
  Calendar,
  Zap,
  Webhook,
  Clock3,
  TrendingUp
} from 'lucide-react';

const FSMAdmin = () => {
  const { token, user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [syncLogs, setSyncLogs] = useState([]);
  const [resourceMappings, setResourceMappings] = useState([]);
  const [unmatchedParts, setUnmatchedParts] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Webhook & Reconciliation status
  const [webhookStatus, setWebhookStatus] = useState(null);
  const [reconciliationStatus, setReconciliationStatus] = useState(null);
  
  const [syncing, setSyncing] = useState(false);
  const [showMappingDialog, setShowMappingDialog] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  
  // OAuth configuration state
  const [showOAuthDialog, setShowOAuthDialog] = useState(false);
  const [grantCode, setGrantCode] = useState('');
  const [oauthLoading, setOauthLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/fsm/appointments/admin/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
    } catch (error) {
      console.error('Error fetching status:', error);
    }
  }, [token]);

  const fetchMappings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/fsm/appointments/admin/resource-mappings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResourceMappings(response.data.mappings || []);
    } catch (error) {
      console.error('Error fetching mappings:', error);
    }
  }, [token]);

  const fetchSyncLogs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/fsm/appointments/sync/logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSyncLogs(response.data.logs || []);
    } catch (error) {
      console.error('Error fetching sync logs:', error);
    }
  }, [token]);

  const fetchUnmatchedParts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/fsm/appointments/admin/unmatched-parts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnmatchedParts(response.data.appointments || []);
    } catch (error) {
      console.error('Error fetching unmatched parts:', error);
    }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users || response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, [token]);

  const fetchWebhookStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/fsm/webhook/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWebhookStatus(response.data);
    } catch (error) {
      console.error('Error fetching webhook status:', error);
    }
  }, [token]);

  const fetchReconciliationStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/fsm/oauth/reconciliation/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReconciliationStatus(response.data);
    } catch (error) {
      console.error('Error fetching reconciliation status:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([
        fetchStatus(),
        fetchMappings(),
        fetchSyncLogs(),
        fetchUnmatchedParts(),
        fetchUsers(),
        fetchWebhookStatus(),
        fetchReconciliationStatus()
      ]);
      setLoading(false);
    };
    loadAll();
  }, [fetchStatus, fetchMappings, fetchSyncLogs, fetchUnmatchedParts, fetchUsers, fetchWebhookStatus, fetchReconciliationStatus]);

  const triggerSync = async () => {
    setSyncing(true);
    try {
      await axios.post(
        `${API}/fsm/appointments/sync/manual`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Sincronização concluída');
      await fetchStatus();
      await fetchSyncLogs();
    } catch (error) {
      console.error('Error syncing:', error);
      toast.error('Erro na sincronização');
    } finally {
      setSyncing(false);
    }
  };

  // OAuth Exchange - called when admin provides Grant Code
  const executeOAuthExchange = async () => {
    if (!grantCode.trim()) {
      toast.error('Introduza o Grant Code');
      return;
    }
    
    setOauthLoading(true);
    try {
      const response = await axios.post(
        `${API}/fsm/oauth/exchange`,
        { grant_code: grantCode.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.status === 'success') {
        toast.success('OAuth configurado com sucesso');
        // Clear grant code immediately - never persist
        setGrantCode('');
        setShowOAuthDialog(false);
        // Refresh status
        await fetchStatus();
      } else {
        toast.error(response.data.message || 'Erro na configuração OAuth');
      }
    } catch (error) {
      // Show sanitized error - never expose tokens or secrets
      const errorMsg = error.response?.data?.detail || 'Erro ao configurar OAuth';
      toast.error(errorMsg);
      console.error('OAuth exchange error (sanitized)');
    } finally {
      setOauthLoading(false);
      // Always clear grant code on completion (success or error)
      setGrantCode('');
    }
  };

  const openMappingDialog = (resource) => {
    setSelectedResource(resource);
    setSelectedUserId(resource.stockflow_user_id || '');
    setShowMappingDialog(true);
  };

  const saveMapping = async () => {
    if (!selectedResource || !selectedUserId) return;
    
    try {
      await axios.post(
        `${API}/fsm/appointments/admin/map-resource`,
        {
          fsm_service_resource_id: selectedResource.fsm_service_resource_id,
          stockflow_user_id: selectedUserId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Mapping guardado');
      setShowMappingDialog(false);
      await fetchMappings();
      await fetchStatus();
    } catch (error) {
      console.error('Error saving mapping:', error);
      toast.error('Erro ao guardar mapping');
    }
  };

  const removeMapping = async (fsm_service_resource_id) => {
    try {
      await axios.delete(
        `${API}/fsm/appointments/admin/map-resource/${fsm_service_resource_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Mapping removido');
      await fetchMappings();
      await fetchStatus();
    } catch (error) {
      console.error('Error removing mapping:', error);
      toast.error('Erro ao remover mapping');
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleString('pt-PT');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="fsm-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Integração Zoho FSM</h1>
          <p className="text-gray-500">Gestão e monitorização da sincronização FSM → StockFlow</p>
        </div>
        <Button onClick={triggerSync} disabled={syncing}>
          <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'A sincronizar...' : 'Sincronizar Agora'}
        </Button>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              {status?.oauth?.configured ? (
                <CheckCircle className="w-8 h-8 text-green-500" />
              ) : (
                <XCircle className="w-8 h-8 text-red-500" />
              )}
              <div className="flex-1">
                <p className="text-sm text-gray-500">OAuth</p>
                <p className="text-lg font-semibold">
                  {status?.oauth?.configured ? 'Configurado' : 'Não configurado'}
                </p>
                {status?.oauth?.configured && (
                  <>
                    <p className={`text-xs ${status.oauth.can_write ? 'text-green-600' : 'text-amber-600'}`} data-testid="oauth-write-status">
                      Catálogo: {status.oauth.can_write ? 'com escrita' : 'só leitura'}
                    </p>
                    <p className={`text-xs ${status.oauth.can_update_wo ? 'text-green-600' : 'text-amber-600'}`} data-testid="oauth-wo-write-status">
                      Ordens de Trabalho e Marcações: {status.oauth.can_update_wo ? 'com escrita' : 'só leitura'}
                    </p>
                  </>
                )}
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-2"
                  onClick={() => setShowOAuthDialog(true)}
                  data-testid="oauth-configure-btn"
                >
                  <Settings className="w-3 h-3 mr-1" />
                  {status?.oauth?.configured ? 'Reautorizar' : 'Configurar OAuth'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Calendar className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-sm text-gray-500">Service Appointments</p>
                <p className="text-lg font-semibold">{status?.appointments?.total || 0}</p>
                <p className="text-xs text-gray-400">
                  {status?.appointments?.open || 0} abertas • {status?.appointments?.closed || 0} fechadas
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-purple-500" />
              <div>
                <p className="text-sm text-gray-500">Resource Mappings</p>
                <p className="text-lg font-semibold">
                  {status?.resource_mappings?.mapped || 0} / {status?.resource_mappings?.total || 0}
                </p>
                {status?.resource_mappings?.unmapped > 0 && (
                  <p className="text-xs text-orange-600">
                    {status.resource_mappings.unmapped} por mapear
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              {status?.unmatched_parts_appointments > 0 ? (
                <AlertTriangle className="w-8 h-8 text-orange-500" />
              ) : (
                <Package className="w-8 h-8 text-green-500" />
              )}
              <div>
                <p className="text-sm text-gray-500">Parts não mapeados</p>
                <p className="text-lg font-semibold">{status?.unmatched_parts_appointments || 0}</p>
                <p className="text-xs text-gray-400">APs com SKUs por resolver</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Resource Mappings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LinkIcon className="w-5 h-5" />
            Mapeamento de Técnicos FSM → StockFlow
          </CardTitle>
          <CardDescription>
            Associe cada Service Resource do Zoho FSM a um utilizador StockFlow
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Técnico FSM</TableHead>
                <TableHead>Resource ID</TableHead>
                <TableHead>Utilizador StockFlow</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {resourceMappings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-gray-500">
                    Nenhum resource encontrado. Execute uma sincronização.
                  </TableCell>
                </TableRow>
              ) : (
                resourceMappings.map(mapping => (
                  <TableRow key={mapping.fsm_service_resource_id}>
                    <TableCell className="font-medium">
                      {mapping.fsm_service_resource_name}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500 font-mono">
                      {mapping.fsm_service_resource_id?.substring(0, 12)}...
                    </TableCell>
                    <TableCell>
                      {mapping.stockflow_user_name || (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {mapping.status === 'mapped' ? (
                        <Badge className="bg-green-100 text-green-800">Mapeado</Badge>
                      ) : (
                        <Badge className="bg-orange-100 text-orange-800">Por mapear</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openMappingDialog(mapping)}
                        >
                          {mapping.status === 'mapped' ? 'Editar' : 'Mapear'}
                        </Button>
                        {mapping.status === 'mapped' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => removeMapping(mapping.fsm_service_resource_id)}
                          >
                            <Unlink className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Unmatched Parts */}
      {unmatchedParts.length > 0 && (
        <Card className="border-orange-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-orange-700">
              <AlertTriangle className="w-5 h-5" />
              SKUs não encontrados ({unmatchedParts.length} APs)
            </CardTitle>
            <CardDescription>
              Estes materiais do FSM não têm correspondência no catálogo StockFlow
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {unmatchedParts.slice(0, 5).map(apt => (
                <div key={apt.id} className="border rounded-lg p-3">
                  <div className="font-medium text-sm mb-2">{apt.ap_number}</div>
                  <div className="space-y-1">
                    {apt.unmatched_parts.map((part, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm bg-orange-50 rounded px-2 py-1">
                        <span>{part.name}</span>
                        <span className="text-gray-500 font-mono text-xs">
                          {part.fsm_sku || 'Sem SKU'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Webhook & Reconciliation Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Webhook Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Webhook className="w-5 h-5" />
              Webhooks FSM
            </CardTitle>
            <CardDescription className="text-xs">
              Eventos em tempo real do Zoho FSM
            </CardDescription>
          </CardHeader>
          <CardContent>
            {webhookStatus ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Total recebidos:</span>
                    <span className="font-medium ml-2">{webhookStatus.total_last_24h || 0}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Processados:</span>
                    <span className="font-medium ml-2 text-green-600">{webhookStatus.last_24h_stats?.processed || 0}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Erros:</span>
                    <span className="font-medium ml-2 text-red-600">{webhookStatus.last_24h_stats?.processed_with_errors || 0}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Rejeitados:</span>
                    <span className="font-medium ml-2 text-gray-500">{webhookStatus.last_24h_stats?.rejected_auth || 0}</span>
                  </div>
                </div>
                {webhookStatus.last_event && (
                  <div className="text-xs text-gray-500 border-t pt-2">
                    Último evento: {new Date(webhookStatus.last_event.received_at).toLocaleString('pt-PT')} ({webhookStatus.last_event.status})
                  </div>
                )}
                {webhookStatus.last_error && (
                  <div className="text-xs text-red-500 bg-red-50 rounded p-2">
                    Último erro: {webhookStatus.last_error.error}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm text-gray-500">A carregar...</div>
            )}
          </CardContent>
        </Card>

        {/* Reconciliation Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Clock3 className="w-5 h-5" />
              Reconciliação Periódica
            </CardTitle>
            <CardDescription className="text-xs">
              Sincronização automática a cada 15 minutos
            </CardDescription>
          </CardHeader>
          <CardContent>
            {reconciliationStatus ? (
              <div className="space-y-3">
                {(reconciliationStatus.sa_cursor || reconciliationStatus.cursor) && (
                  <div className="text-sm space-y-2">
                    <div className="font-medium text-gray-700">Service Appointments</div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Última execução:</span>
                      <span className="font-medium">
                        {(reconciliationStatus.sa_cursor || reconciliationStatus.cursor)?.last_successful_reconciliation 
                          ? new Date((reconciliationStatus.sa_cursor || reconciliationStatus.cursor).last_successful_reconciliation).toLocaleString('pt-PT')
                          : 'Nunca'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Resultado:</span>
                      {(reconciliationStatus.sa_cursor || reconciliationStatus.cursor)?.last_result === 'success' ? (
                        <Badge className="bg-green-100 text-green-800 text-xs">Sucesso</Badge>
                      ) : (reconciliationStatus.sa_cursor || reconciliationStatus.cursor)?.last_result === 'partial' ? (
                        <Badge className="bg-yellow-100 text-yellow-800 text-xs">Parcial</Badge>
                      ) : (reconciliationStatus.sa_cursor || reconciliationStatus.cursor)?.last_result === 'failed' ? (
                        <Badge className="bg-red-100 text-red-800 text-xs">Falhou</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">Pendente</Badge>
                      )}
                    </div>
                  </div>
                )}
                {reconciliationStatus.wo_cursor && (
                  <div className="text-sm space-y-2 border-t pt-2">
                    <div className="font-medium text-gray-700">Work Orders</div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Última execução:</span>
                      <span className="font-medium">
                        {reconciliationStatus.wo_cursor.last_successful_reconciliation 
                          ? new Date(reconciliationStatus.wo_cursor.last_successful_reconciliation).toLocaleString('pt-PT')
                          : 'Nunca'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Resultado:</span>
                      {reconciliationStatus.wo_cursor.last_result === 'success' ? (
                        <Badge className="bg-green-100 text-green-800 text-xs">Sucesso</Badge>
                      ) : reconciliationStatus.wo_cursor.last_result === 'partial' ? (
                        <Badge className="bg-yellow-100 text-yellow-800 text-xs">Parcial</Badge>
                      ) : reconciliationStatus.wo_cursor.last_result === 'failed' ? (
                        <Badge className="bg-red-100 text-red-800 text-xs">Falhou</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">Pendente</Badge>
                      )}
                    </div>
                  </div>
                )}
                {reconciliationStatus.last_24h_stats && Object.keys(reconciliationStatus.last_24h_stats).length > 0 && (
                  <div className="grid grid-cols-2 gap-2 text-sm border-t pt-2">
                    <div>
                      <span className="text-gray-500">Execuções (24h):</span>
                      <span className="font-medium ml-2">{reconciliationStatus.last_24h_stats.total_runs || 0}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">SAs sincronizadas:</span>
                      <span className="font-medium ml-2 text-green-600">{reconciliationStatus.last_24h_stats.total_sas_synced || reconciliationStatus.last_24h_stats.total_synced || 0}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">WOs processados:</span>
                      <span className="font-medium ml-2 text-blue-600">{reconciliationStatus.last_24h_stats.total_wos_synced || 0}</span>
                    </div>
                  </div>
                )}
                {!reconciliationStatus.sa_cursor && !reconciliationStatus.cursor && !reconciliationStatus.wo_cursor && (
                  <div className="text-sm text-gray-500">Aguardando primeira execução...</div>
                )}
              </div>
            ) : (
              <div className="text-sm text-gray-500">A carregar...</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Sync Logs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Histórico de Sincronizações
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Processadas</TableHead>
                <TableHead>Criadas</TableHead>
                <TableHead>Atualizadas</TableHead>
                <TableHead>Parts Matched</TableHead>
                <TableHead>Duração</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {syncLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-gray-500">
                    Nenhuma sincronização executada
                  </TableCell>
                </TableRow>
              ) : (
                syncLogs.map(log => (
                  <TableRow key={log.id}>
                    <TableCell className="text-sm">{formatDate(log.started_at)}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{log.sync_type}</Badge>
                    </TableCell>
                    <TableCell>
                      {log.status === 'success' ? (
                        <Badge className="bg-green-100 text-green-800">Sucesso</Badge>
                      ) : log.status === 'partial' ? (
                        <Badge className="bg-yellow-100 text-yellow-800">Parcial</Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-800">Falhou</Badge>
                      )}
                    </TableCell>
                    <TableCell>{log.appointments_processed}</TableCell>
                    <TableCell className="text-green-600">+{log.appointments_created}</TableCell>
                    <TableCell className="text-blue-600">{log.appointments_updated}</TableCell>
                    <TableCell>
                      {log.parts_matched}/{log.parts_processed}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {log.duration_ms ? `${log.duration_ms}ms` : '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* OAuth Configuration Dialog */}
      <Dialog open={showOAuthDialog} onOpenChange={(open) => {
        setShowOAuthDialog(open);
        if (!open) setGrantCode(''); // Clear on close
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Configurar OAuth Zoho FSM</DialogTitle>
            <DialogDescription>
              Introduza o Grant Code gerado na Zoho API Console para autorizar a integração.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Grant Code</label>
              <Input
                type="password"
                placeholder="Cole aqui o Grant Code..."
                value={grantCode}
                onChange={(e) => setGrantCode(e.target.value)}
                autoComplete="off"
                data-testid="oauth-grant-code-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                O Grant Code expira em 3 minutos. Não será guardado.
              </p>
              <p className="text-xs text-gray-500 mt-3">Scopes a indicar na Zoho API Console:</p>
              <code className="block text-[11px] bg-gray-100 rounded p-2 mt-1 break-all select-all" data-testid="oauth-scopes-hint">
                ZohoFSM.modules.ServiceAppointments.READ,ZohoFSM.modules.ServiceAppointments.UPDATE,ZohoFSM.modules.WorkOrders.READ,ZohoFSM.modules.WorkOrders.UPDATE,ZohoFSM.modules.Contacts.READ,ZohoFSM.modules.Companies.READ,ZohoFSM.modules.Service_And_Parts.READ,ZohoFSM.modules.Service_And_Parts.CREATE,ZohoFSM.modules.Service_And_Parts.UPDATE,ZohoFSM.modules.custom.READ
              </code>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowOAuthDialog(false);
                setGrantCode('');
              }}
              disabled={oauthLoading}
            >
              Cancelar
            </Button>
            <Button 
              onClick={executeOAuthExchange} 
              disabled={!grantCode.trim() || oauthLoading}
              data-testid="oauth-authorize-btn"
            >
              {oauthLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  A autorizar...
                </>
              ) : (
                'Autorizar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mapping Dialog */}
      <Dialog open={showMappingDialog} onOpenChange={setShowMappingDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mapear Técnico FSM</DialogTitle>
            <DialogDescription>
              Associe o técnico FSM "{selectedResource?.fsm_service_resource_name}" a um utilizador StockFlow
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Utilizador StockFlow</label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um utilizador" />
                </SelectTrigger>
                <SelectContent>
                  {users.filter(u => ['technician', 'store', 'admin', 'manager', 'backoffice'].includes(u.role)).map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.full_name || u.email} ({u.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMappingDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={saveMapping} disabled={!selectedUserId}>
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FSMAdmin;
